import { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge, fmtDateTime,
} from "../../components/app/Kit";

export default function AIAgents() {
  const { data, state, error, reload, setData } = useResource("/ai-agents");
  const [open, setOpen] = useState(null);

  const toggle = async (agent) => {
    try {
      const enabled = !agent.enabled;
      const { data: updated } = await api.patch(`/ai-agents/${agent.id}`, {
        enabled,
        status: enabled ? "active" : "needs_configuration",
      });
      setData((list) => list.map((a) => (a.id === agent.id ? updated : a)));
      toast.success(`${agent.name} ${enabled ? "enabled" : "disabled"}.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="ai-agents-page">
      <PageTitle
        testId="ai-agents-title"
        title="AI Agents"
        sub="Build AI employees that work alongside your team — each with a defined purpose, channels, tools and escalation rules."
        action={<DemoBadge />}
      />

      <div
        data-testid="agents-provider-notice"
        className="mb-4 rounded-lg border border-amber-300/25 bg-amber-300/[0.05] p-4 text-xs text-amber-200"
      >
        <span className="font-medium">No AI provider connected.</span> Agent configuration is stored, but agents
        will not run until a voice/AI provider (for example Vapi) is connected with your own credentials.
      </div>

      {state === "loading" ? <Loading rows={7} testId="ai-agents-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="ai-agents-error" /> : null}
      {state === "ready" && data.length === 0 ? (
        <EmptyState icon="Bot" title="No agents configured" sub="Agents are created during onboarding based on your industry template." testId="ai-agents-empty" />
      ) : null}

      {state === "ready" && data.length > 0 ? (
        <div className="grid gap-2 lg:grid-cols-2">
          {data.map((a) => (
            <div key={a.id} data-testid={`agent-${a.id}`} className="glass rounded-lg p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-elv-text">
                    <Icons.Bot className="h-4 w-4 text-elv-green" />
                    {a.name}
                  </p>
                  <p className="mt-2 max-w-md text-[11px] text-elv-dim">{a.purpose}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone={a.status === "active" ? "green" : "amber"}>{a.status.replace(/_/g, " ")}</Pill>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={a.enabled}
                    onClick={() => toggle(a)}
                    data-testid={`agent-toggle-${a.id}`}
                    className={`relative h-5 w-9 shrink-0 rounded-full border transition-colors duration-300 ${
                      a.enabled ? "border-elv-green/50 bg-elv-green/30" : "border-white/10 bg-white/5"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform duration-300 ${
                        a.enabled ? "translate-x-[18px] bg-elv-green" : "translate-x-0.5 bg-white/40"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Field label="Channels" items={a.channels} />
                <Field label="Tools" items={a.tools} />
              </div>

              <p className="mt-4 font-mono text-[10px] text-elv-dim">Last activity: {fmtDateTime(a.last_activity)}</p>

              <button
                type="button"
                onClick={() => setOpen(open === a.id ? null : a.id)}
                data-testid={`agent-details-toggle-${a.id}`}
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan"
              >
                {open === a.id ? "Hide" : "View"} instructions & escalation
                <Icons.ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open === a.id ? "rotate-180" : ""}`} />
              </button>

              {open === a.id ? (
                <div data-testid={`agent-details-${a.id}`} className="mt-3 grid gap-3 border-t border-elv-line pt-3">
                  <Block label="Instructions" body={a.instructions} />
                  <Field label="Knowledge" items={a.knowledge} />
                  <Block label="Escalation rules" body={a.escalation} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const Field = ({ label, items }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">{label}</p>
    <div className="mt-2 flex flex-wrap gap-1.5">
      {(items || []).map((i) => (
        <span key={i} className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2 py-0.5 text-[10px] text-elv-dim">
          {i}
        </span>
      ))}
    </div>
  </div>
);

const Block = ({ label, body }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.16em] text-elv-dim">{label}</p>
    <p className="mt-2 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 font-mono text-[10px] leading-relaxed text-elv-dim">
      {body}
    </p>
  </div>
);
